import {
  AuthResponse,
  PasswordlessStartResponse,
  StartPasswordlessLoginParams,
  FinishPasswordlessLoginParams,
  UpdateUserProfileParams,
  LoginWithPasswordParams,
  ChangePasswordParams,
  ValidateUserCodeParams,
  ApproveDeviceCodeParams,
  OtpLoginParams,
  UserData,
} from "types";
import { getPlatform, getApplicationId, getConfigId } from "../storage";
import { appendQueryParam } from "utils/url";
import { getUserAgentHeaders } from "utils/headers";
import { dspBaseUrl } from "env";

const getJsonContentTypeHeaders = (): HeadersInit => ({
  "Content-Type": "application/json",
  ...getUserAgentHeaders(),
});

const SERVER_ERROR_LOG_PREFIX = "Server error:";

interface ServerErrorBody {
  error?: string;
  code?: string;
  fieldErrors?: Record<string, string>;
}

export interface ServerError extends Error {
  code?: string;
  fieldErrors?: Record<string, string>;
}

// Matches the `code` the DSP's recaptcha middleware sets whenever it rejects
// a request for a missing/invalid/low-score token — including the case where
// its cached "recaptcha enabled" flag hasn't caught up with Strapi yet, so a
// client that correctly skipped the widget still gets rejected.
export const RECAPTCHA_REQUIRED_CODE = "RECAPTCHA_REQUIRED";

export function isRecaptchaRequiredError(err: unknown): boolean {
  return err instanceof Error && (err as ServerError).code === RECAPTCHA_REQUIRED_CODE;
}

// Matches the `code` the DSP sets on genuine infra/config failures
// (get_fusionauth_config() errors, FusionAuth unreachable, unhandled
// exceptions) — distinct from a rejected login attempt, since telling a
// user their credentials were wrong when the real cause is our server
// being broken sends them retrying pointlessly instead of waiting or
// contacting support.
export const SERVICE_UNAVAILABLE_CODE = "SERVICE_UNAVAILABLE";

export function isServiceUnavailableError(err: unknown): boolean {
  return err instanceof Error && (err as ServerError).code === SERVICE_UNAVAILABLE_CODE;
}

// Backend failures carry a useful body ({ error, code, fieldErrors } — see
// fast_handler_fusion_common.error() in the DSP repo) that callers need to
// show the real cause (e.g. a stale recaptcha-enabled cache, a missing
// profile field) instead of a generic message.
async function toServerError(res: Response, fallbackMessage: string): Promise<ServerError> {
  const body: ServerErrorBody = await res.json().catch(() => ({}));
  console.error(SERVER_ERROR_LOG_PREFIX, body);
  const err = new Error(body.error || fallbackMessage) as ServerError;
  err.code = body.code;
  err.fieldErrors = body.fieldErrors;
  return err;
}

// Every backend call is expected to carry the `platform`/`tenant`,
// `application_id`/`app_id`, and `config_id` captured from the URL (see
// useLoginPage.ts / storage.ts) alongside whatever other query params it has.
function withPlatform(url: string): string {
  let result = url;
  const platform = getPlatform();
  if (platform) result = appendQueryParam(result, "platform", platform);
  const applicationId = getApplicationId();
  if (applicationId) result = appendQueryParam(result, "application_id", applicationId);
  const configId = getConfigId();
  if (configId) result = appendQueryParam(result, "configid", configId);
  return result;
}

export async function startPasswordlessLogin({
  loginId,
  recaptchaToken,
  enableRecaptcha,
  url,
}: StartPasswordlessLoginParams): Promise<PasswordlessStartResponse> {
  const baseUrl = url ?? `${dspBaseUrl}/passwordless/start`;
  const headers: HeadersInit = { ...getJsonContentTypeHeaders() };

  if (recaptchaToken) {
    (headers as Record<string, string>)["x-recaptcha-token"] = recaptchaToken;
  }

  if (!enableRecaptcha) {
    (headers as Record<string, string>)["x-skip-recaptcha"] = "true";
  }

  const res = await fetch(withPlatform(baseUrl), {
    method: "POST",
    headers,
    body: JSON.stringify({ loginId }),
  });
  if (!res.ok) {
    throw await toServerError(res, "Failed to start passwordless login");
  }
  return res.json() as Promise<PasswordlessStartResponse>;
}

export async function finishPasswordlessLogin({
  code,
  oneTimeCode,
  url,
}: FinishPasswordlessLoginParams): Promise<AuthResponse> {
  try {
    const baseUrl = url ?? `${dspBaseUrl}/passwordless/login`;
    const res = await fetch(withPlatform(baseUrl), {
      method: "POST",
      headers: getJsonContentTypeHeaders(),
      body: JSON.stringify({ code, oneTimeCode }),
    });
    if (!res.ok) {
      throw await toServerError(res, "Failed to login");
    }
    return res.json(); // { access_token, refresh_token, ... }
  } catch (err) {
    console.error("finishPasswordlessLogin error:", err);
    throw err;
  }
}

export async function updateUserProfile({
  firstName,
  lastName,
  password,
  userId,
  url,
  accessToken,
  approveMarketing,
  approveTermsOfUse
}: UpdateUserProfileParams): Promise<UserData> {
  const baseUrl = url ?`${url}/${userId}`: `${dspBaseUrl}/user/${userId}`;
  const res = await fetch(withPlatform(baseUrl), {
    method: "PATCH",
    headers: { ...getJsonContentTypeHeaders(), authorization: accessToken },
    body: JSON.stringify({
      firstName,
      lastName,
      password,
      approveMarketing,
      approveTermsOfUse
    }),
  });
  if (!res.ok) {
    throw await toServerError(res, "Failed to update profile");
  }
  return await res.json();
}

export async function loginWithPassword({
  loginId,
  password,
  recaptchaToken,
  enableRecaptcha,
  url,
}: LoginWithPasswordParams): Promise<AuthResponse> {
  try {
    const body = {
      loginId,
      password,
    };

    const baseUrl = url ?? `${dspBaseUrl}/login`;
    const headers: HeadersInit = {
      ...getJsonContentTypeHeaders(),
      Accept: "application/json",
    };
    if (recaptchaToken) {
      (headers as Record<string, string>)["x-recaptcha-token"] = recaptchaToken;
    }
    if (!enableRecaptcha) {
      (headers as Record<string, string>)["x-skip-recaptcha"] = "true";
    }
    const res = await fetch(withPlatform(baseUrl), {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      throw await toServerError(res, "Failed to login with password");
    }
    return res.json();
  } catch (err) {
    console.error("loginWithPassword error:", err);
    throw err;
  }
}

export async function forgotPassword(email: string, url?: string): Promise<unknown> {
  try {
    const baseUrl = url ?? `${dspBaseUrl}/user/forgot-password`;
    const res = await fetch(withPlatform(baseUrl), {
      method: "POST",
      headers: getJsonContentTypeHeaders(),
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      console.error(SERVER_ERROR_LOG_PREFIX, error);
      throw new Error("Failed to send forgot password email");
    }
    return res.json();
  } catch (err) {
    console.error("forgotPassword error:", err);
    throw err;
  }
}

export async function changePasswordWithPasswordId({
  passwordId,
  password,
  loginId,
  url,
}: ChangePasswordParams): Promise<{ oneTimePassword: string }> {
  try {
    const baseUrl = url ?? `${dspBaseUrl}/user/change-password`;
    const res = await fetch(withPlatform(baseUrl), {
      method: "POST",
      headers: getJsonContentTypeHeaders(),
      body: JSON.stringify({ changePasswordId: passwordId, password, loginId }),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      console.error(SERVER_ERROR_LOG_PREFIX, error);
      throw new Error("Failed to change password");
    }
    return res.json();
  } catch (err) {
    console.error("changePasswordWithPasswordId error:", err);
    throw err;
  }
}

export async function loginWithOtp({ oneTimePassword, url }: OtpLoginParams): Promise<AuthResponse> {
  try {
    const baseUrl = url ?? `${dspBaseUrl}/otp/login`;
    const res = await fetch(withPlatform(baseUrl), {
      method: "POST",
      headers: getJsonContentTypeHeaders(),
      body: JSON.stringify({ oneTimePassword }),
    });
    if (!res.ok) {
      throw await toServerError(res, "Failed to login with OTP");
    }
    return res.json();
  } catch (err) {
    console.error("loginWithOtp error:", err);
    throw err;
  }
}

export async function validateUserCode({ user_code, url }: ValidateUserCodeParams): Promise<{ valid: true }> {
  const baseUrl = url ?? `${dspBaseUrl}/device/validate`;
  const requestUrl = `${baseUrl}?user_code=${encodeURIComponent(user_code)}`;
  const res = await fetch(withPlatform(requestUrl), {
    method: "GET",
    headers: getUserAgentHeaders(),
  });
  if (res.status !== 200) {
    throw new Error("Invalid or expired user code");
  }
  return { valid: true };
}

export async function approveDeviceCode({ user_code, access_token, url }: ApproveDeviceCodeParams): Promise<string> {
  const headers = getJsonContentTypeHeaders();

  const baseUrl = url ?? `${dspBaseUrl}/device/approve`;

  const res = await fetch(withPlatform(baseUrl), {
    method: "POST",
    headers,
    body: JSON.stringify({
      user_code,
      token: access_token,
    }),
  });

  if (!res.ok) {
    throw new Error("Failed to approve device code");
  }

  return await res.text();
}
